import React, { lazy, Suspense, useState } from "react";

/**
 * Code-split wrappers for UI that is NOT visible on the first load of the public storefront:
 * the Studio section editors, the guided tour, the focal-point picker, and the modals.
 *
 * Every wrapper has the same props as the real component and brings its own <Suspense>, so the
 * (huge) catalog page never blanks while a chunk loads. Nothing above the fold lives here.
 */

const loadEditors = () => import("./CatalogStudioSectionEditors");

/** Start downloading the Studio editors chunk (call as soon as we know we are in the Studio). */
export const preloadStudioEditors = loadEditors;

function lazyStudio(name) {
  const L = lazy(() => loadEditors().then((m) => ({ default: m[name] })));
  const Wrapped = (props) => (
    <Suspense fallback={null}>
      <L {...props} />
    </Suspense>
  );
  Wrapped.displayName = `LazyStudio(${name})`;
  return Wrapped;
}

function lazyDefault(loader, name) {
  const L = lazy(loader);
  const Wrapped = (props) => (
    <Suspense fallback={null}>
      <L {...props} />
    </Suspense>
  );
  Wrapped.displayName = `Lazy(${name})`;
  return Wrapped;
}

/**
 * Modals: nothing is fetched or mounted until the first time `openProp` is truthy; after that it stays
 * mounted (so the component keeps its own open/close handling and state).
 */
function lazyModal(loader, name, openProp) {
  const L = lazy(loader);
  const Wrapped = (props) => {
    const open = Boolean(props[openProp]);
    const [seen, setSeen] = useState(open);
    if (open && !seen) setSeen(true);
    if (!seen) return null;
    return (
      <Suspense fallback={null}>
        <L {...props} />
      </Suspense>
    );
  };
  Wrapped.displayName = `LazyModal(${name})`;
  return Wrapped;
}

// ---- Studio section editors (one chunk: CatalogStudioSectionEditors) ----
export const StudioPanel = lazyStudio("StudioPanel");
export const StudioField = lazyStudio("StudioField");
export const StudioInput = lazyStudio("StudioInput");
export const StudioSelect = lazyStudio("StudioSelect");
export const StudioTextarea = lazyStudio("StudioTextarea");
export const StudioColorField = lazyStudio("StudioColorField");
export const StudioSectionFrameSettings = lazyStudio("StudioSectionFrameSettings");
export const StudioSectionHeadingFields = lazyStudio("StudioSectionHeadingFields");
export const StudioFaqEditor = lazyStudio("StudioFaqEditor");
export const StudioCountdownEditor = lazyStudio("StudioCountdownEditor");
export const StudioBrandsAppearanceFields = lazyStudio("StudioBrandsAppearanceFields");
export const StudioStatsEditor = lazyStudio("StudioStatsEditor");
export const StudioDealsProductsEditor = lazyStudio("StudioDealsProductsEditor");
export const StudioCategoryItemsEditor = lazyStudio("StudioCategoryItemsEditor");
export const StudioCircleItemsEditor = lazyStudio("StudioCircleItemsEditor");
export const StudioTrustItemsEditor = lazyStudio("StudioTrustItemsEditor");
export const StudioPromoBannerEditor = lazyStudio("StudioPromoBannerEditor");
export const StudioSlidesEditor = lazyStudio("StudioSlidesEditor");
export const StudioBlogPostsEditor = lazyStudio("StudioBlogPostsEditor");
export const StudioAboutCardsEditor = lazyStudio("StudioAboutCardsEditor");
export const StudioCategoryLayoutFields = lazyStudio("StudioCategoryLayoutFields");
export const StudioCarouselOverlaySettings = lazyStudio("StudioCarouselOverlaySettings");
export const StudioCarouselSectionEditor = lazyStudio("StudioCarouselSectionEditor");
export const StudioInfoCardsEditor = lazyStudio("StudioInfoCardsEditor");

// ---- Other Studio-only UI ----
export const CatalogStudioTour = lazyDefault(() => import("./CatalogStudioTour"), "CatalogStudioTour");
export const HeroImageFocalPicker = lazyDefault(() => import("./HeroImageFocalPicker"), "HeroImageFocalPicker");
export const CatalogStudioSectionPickerSkeleton = lazyDefault(
  () => import("./CatalogStudioSectionPickerSkeleton"),
  "CatalogStudioSectionPickerSkeleton"
);
export const StudioCustomOrderEditor = lazyDefault(
  () => import("./catalog/StudioCustomOrderEditor").then((m) => ({ default: m.StudioCustomOrderEditor })),
  "StudioCustomOrderEditor"
);
export const StudioBundleOffersEditor = lazyDefault(
  () => import("./catalog/StudioBundleOffersEditor").then((m) => ({ default: m.StudioBundleOffersEditor })),
  "StudioBundleOffersEditor"
);

// ---- Modals (not visible until the shopper/merchant opens them) ----
const loadConfirmModal = () => import("./ConfirmModal");
const loadErrorModal = () => import("./ErrorModal");
const loadWholesaleAuthModal = () => import("./catalog/CatalogWholesaleAuthModal");
const loadWholesaleOrdersModal = () => import("./catalog/CatalogWholesaleOrdersModal");

export const ConfirmModal = lazyModal(loadConfirmModal, "ConfirmModal", "isOpen");
export const ErrorModal = lazyModal(loadErrorModal, "ErrorModal", "isOpen");
export const CatalogWholesaleAuthModal = lazyModal(loadWholesaleAuthModal, "CatalogWholesaleAuthModal", "open");
export const CatalogWholesaleOrdersModal = lazyModal(loadWholesaleOrdersModal, "CatalogWholesaleOrdersModal", "open");

/**
 * Warm the shopper-facing modal chunks once the page is idle, so the first click doesn't wait on the network.
 * (Called from an idle callback, i.e. after first paint — never competes with the hero / LCP.)
 */
export function preloadStorefrontModals() {
  loadErrorModal();
  loadWholesaleAuthModal();
  loadWholesaleOrdersModal();
}
